// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_encore/recap-year
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). The ledger stays local; only a DERIVED-SIGNALS
// object (counts, spend totals, artist/venue frequency, gaps, year deltas — see
// signals.rs) is sent for THIS recap call. Setlist notes / free recollection never
// leave the device. ONE billed call per recap (CRUD-not-agentic).
//
// HARDEST guardrail — the FENCE (CAN-safe, observational):
//   • The recap DESCRIBES shows the user ALREADY ATTENDED and spend they LOGGED. It is a
//     "Year in Live Music" narration of THEIR ledger.
//   • It NEVER advises, NEVER recommends a show/artist/festival to attend, NEVER suggests
//     buying or reselling tickets, NEVER estimates resale value, NEVER acts as a
//     marketplace. No resale/marketplace, no ticket-buying.
//   • This ceiling is mirrored server-side in the prompt (below) AND enforced PRE-LLM here:
//     fenceCheck() blocks any free-text the user could route to the model toward a banned
//     intent before a billed call is ever made. The recap itself sends only aggregates, so
//     there is no user free-text on the recap path — the gate exists for safety + future
//     "ask about my ledger" surfaces.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { LedgerSignals } from "./store";

const SERVER = "https://api.orioralabs.com";
const APP_SLUG = "oriora_encore";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "refused" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

// ── THE FENCE (pre-LLM) ────────────────────────────────────────────────────────
// Deterministic refusal BEFORE any billed call. Encore is observational-only: it
// describes the user's own attended-show ledger. Anything steering the model toward
// buying / reselling / pricing tickets, or acting as a marketplace, is refused here —
// no network call, no spend. Returns null if clean, or a refusal message if blocked.
const BANNED_INTENT = [
  /\b(buy|purchase|order|book)\b.{0,20}\b(ticket|tickets|seat|seats|pass)\b/i,
  /\b(resell|resale|re-sell|sell|flip|scalp|scalping|tout)\b.{0,20}\b(ticket|tickets|seat|seats|pass)\b/i,
  /\b(ticket|tickets|seat|seats)\b.{0,20}\b(for sale|resale|resell|value|worth|price|market)\b/i,
  /\bhow much (can|could|would) i (get|sell|make)\b/i,
  /\b(stubhub|ticketmaster|viagogo|seatgeek|secondary market|marketplace)\b/i,
  /\b(recommend|suggest|should i (go|see|attend|buy))\b/i,
];
export function fenceCheck(userText: string): string | null {
  const t = (userText ?? "").trim();
  if (!t) return null;
  for (const re of BANNED_INTENT) {
    if (re.test(t)) {
      return "Encore only describes shows you've already logged — it doesn't buy, sell, price, or recommend tickets.";
    }
  }
  return null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface YearRecap {
  recap: string;
}

/**
 * "Year in Live Music" recap. The ONE AI feature (one billed call per recap).
 *
 * We compute the non-obvious STRUCTURE on-device (per-year counts, artist/venue/city
 * frequency, festival-vs-regular spend, longest gap, year-over-year deltas — see
 * store.ts / signals.rs) and send ONLY those small aggregates. The brain narrates a
 * warm, specific "Year in Live Music" that NAMES the figures the user couldn't spot at
 * a glance. Setlist notes never leave the device.
 *
 * Endpoint: POST /api/apps/oriora_encore/recap-year (Bearer JWT). The server prompt
 * (RECAP_SYSTEM_PROMPT below, operator task) is OBSERVATIONAL ONLY — it describes
 * attended shows + logged spend, names the numbers, and obeys the fence (no buying,
 * reselling, pricing, recommending). Optional `scope` lets the UI ask for the whole
 * ledger ("all") or a single year.
 */
export async function recapYear(signals: LedgerSignals, scope: "all" | number = "all"): Promise<YearRecap> {
  if (signals.overall.total_shows === 0) {
    throw new BrainError("unavailable", "Log a few shows first — there's nothing to recap yet.");
  }
  const r = await brainCall<Partial<YearRecap>>("recap-year", { signals, scope });
  return { recap: String(r.recap ?? "").trim() };
}

// The system prompt the SERVER must use for /api/apps/oriora_encore/recap-year (operator
// task — mirrored here so the contract + fence live with the client). Observational,
// names the figures, holds the refusal fence.
export const RECAP_SYSTEM_PROMPT = `You are Encore's "Year in Live Music" narrator. You receive a derived-signals object computed on the user's own device from the live shows THEY ATTENDED and logged: counts, distinct artists/venues/cities, per-year breakdowns, most-seen artists, venue/city frequency, festival-vs-regular spend totals (amounts are in MINOR units / cents of the stated currency), a calendar-month histogram, the longest gap between shows, and rating stats.

Write a warm, vivid recap (3-6 sentences) that NAMES specific figures from the data — e.g. "You saw 23 shows this year across 14 venues; you've now seen The National 6 times, your most-seen artist; festivals were your biggest spend; your longest gap between shows was August." Convert cents to the user's currency when you mention money. Lead with the headline counts, then the standout pattern (a most-seen artist, a venue you keep returning to, the festival-vs-regular spend split, a notable gap, your highest-rated night).

HARD RULES — never break these:
- DESCRIBE ONLY shows already attended and spend already logged. This is a recap of the user's past, not a plan.
- NEVER recommend or suggest a show, artist, festival, or venue to attend.
- NEVER help buy, book, sell, resell, flip, or price tickets; NEVER estimate resale or market value; NEVER act as a marketplace or mention secondary-market sites.
- No advice, no judgement of whether spending or attendance is good/bad — just the observed facts, warmly told.
If the data is sparse, say what you can and note that more logged shows make the recap richer. Output only the recap prose (no headings, no lists).`;

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `oriora-encore` as a return target (server-side, operator task).
// ── Cloud backup (the user owns their data) — fleet standard ────────────────────
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }
async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}
/** Save the local backup envelope to the user's connected cloud store (GitHub/Drive). */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ data: envelope }) });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}
/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_encore/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }) });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

export const openWebLogin = () => invoke<void>("open_login_window");
